import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Lock, CheckCircle, AlertCircle, Loader2, Tv, Smartphone, Hash, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { ONBOARDING_KEY } from '../components/Client/ClientOnboarding';
import { VENDOR_ONBOARDING_KEY } from '../components/Vendor/VendorOnboarding';

const AccountSettings = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  // Profile update state
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || '',
    phone: '',
    device_type: '',
    iptv_app: '',
    mac_address: '',
    device_id: '',
    snapchat: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  
  // Email change state
  const [emailData, setEmailData] = useState({
    new_email: '',
    current_password: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      await api.put('/user/profile', profileData);
      setProfileSuccess('Profil mis à jour avec succès !');
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setProfileError(err.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
    } finally {
      setProfileLoading(false);
    }
  };

  // Charger les données du profil au montage
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/user/profile-data');
        setProfileData({
          full_name: res.data.full_name || '',
          phone: res.data.phone || '',
          device_type: res.data.device_type || '',
          iptv_app: res.data.iptv_app || '',
          mac_address: res.data.mac_address || '',
          device_id: res.data.device_id || '',
          snapchat: res.data.snapchat || ''
        });
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setProfileLoaded(true);
      }
    };
    fetchProfile();
  }, []);

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess('');
    setEmailLoading(true);

    try {
      const response = await api.post('/user/request-email-change', emailData);
      setEmailSuccess(response.data.message);
      setEmailData({ new_email: '', current_password: '' });
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Erreur lors de la demande de changement d\'email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordChangeRequest = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    setPasswordLoading(true);

    try {
      const response = await api.post('/user/request-password-change');
      setPasswordSuccess(response.data.message);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Erreur lors de la demande de changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'password', label: 'Mot de passe', icon: Lock }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#16161A] rounded-xl shadow-xl border border-[#3A3A3D] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] p-6">
            <h1 className="text-3xl font-bold text-white">⚙️ Paramètres du Compte</h1>
            <p className="text-white/80 mt-2">Gérez vos informations personnelles</p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#3A3A3D]">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#222226] text-[#00FFFF] border-b-2 border-[#00FFFF]'
                      : 'text-[#B8B8BA] hover:bg-[#1A1A1D] hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Modifier votre profil</h2>
                
                {profileSuccess && (
                  <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-5 w-5" />
                    <span>{profileSuccess}</span>
                  </div>
                )}
                
                {profileError && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span>{profileError}</span>
                  </div>
                )}

                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                        Nom complet
                      </label>
                      <input
                        type="text"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                        placeholder="Votre nom complet"
                        required
                        minLength={2}
                        maxLength={100}
                        data-testid="profile-fullname"
                      />
                    </div>
                    <div>
                      <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                        placeholder="Votre numéro de téléphone"
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                      Snapchat
                    </label>
                    <input
                      type="text"
                      value={profileData.snapchat}
                      onChange={(e) => setProfileData({ ...profileData, snapchat: e.target.value })}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                      placeholder="Votre pseudo Snapchat"
                      data-testid="profile-snapchat"
                    />
                  </div>

                  <div className="border-t border-[#3A3A3D] pt-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Informations appareil</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                          Type d'appareil
                        </label>
                        <input
                          type="text"
                          value={profileData.device_type}
                          onChange={(e) => setProfileData({ ...profileData, device_type: e.target.value })}
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                          placeholder="Smart TV, Box Android..."
                          data-testid="profile-device-type"
                        />
                      </div>
                      <div>
                        <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                          Application IPTV
                        </label>
                        <input
                          type="text"
                          value={profileData.iptv_app}
                          onChange={(e) => setProfileData({ ...profileData, iptv_app: e.target.value })}
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                          placeholder="IPTV Smarters, TiviMate..."
                          data-testid="profile-iptv-app"
                        />
                      </div>
                      <div>
                        <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                          Adresse MAC
                        </label>
                        <input
                          type="text"
                          value={profileData.mac_address}
                          onChange={(e) => setProfileData({ ...profileData, mac_address: e.target.value })}
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                          placeholder="AA:BB:CC:DD:EE:FF"
                          data-testid="profile-mac-address"
                        />
                      </div>
                      <div>
                        <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">
                          Device ID
                        </label>
                        <input
                          type="text"
                          value={profileData.device_id}
                          onChange={(e) => setProfileData({ ...profileData, device_id: e.target.value })}
                          className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                          placeholder="Identifiant de l'appareil"
                          data-testid="profile-device-id"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={profileLoading}
                    className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FF8C5A] text-white py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-orange-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="profile-save-btn"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Mise à jour...
                      </>
                    ) : (
                      'Enregistrer les modifications'
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Email Tab */}
            {activeTab === 'email' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Modifier votre adresse email</h2>
                
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/50 rounded-lg">
                  <p className="text-blue-400 text-sm">
                    📧 Adresse actuelle : <strong>{user?.email}</strong>
                  </p>
                </div>
                
                {emailSuccess && (
                  <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-5 w-5" />
                    <span>{emailSuccess}</span>
                  </div>
                )}
                
                {emailError && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span>{emailError}</span>
                  </div>
                )}

                <form onSubmit={handleEmailChange} className="space-y-6">
                  <div>
                    <label className="block text-[#E8E8E8] font-medium mb-2">
                      Nouvelle adresse email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                      <input
                        type="email"
                        value={emailData.new_email}
                        onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                        placeholder="nouvelle@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#E8E8E8] font-medium mb-2">
                      Mot de passe actuel (pour confirmer)
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                      <input
                        type="password"
                        value={emailData.current_password}
                        onChange={(e) => setEmailData({ ...emailData, current_password: e.target.value })}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                        placeholder="Votre mot de passe actuel"
                        required
                        autoComplete="current-password"
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      ⚠️ Un email de vérification sera envoyé à votre nouvelle adresse. Vous devrez cliquer sur le lien pour finaliser le changement.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={emailLoading}
                    className="w-full bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {emailLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      'Demander le changement'
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Modifier votre mot de passe</h2>
                
                <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/50 rounded-lg">
                  <p className="text-blue-400 text-sm">
                    🔐 Pour des raisons de sécurité, vous recevrez un lien par email pour définir votre nouveau mot de passe.
                  </p>
                </div>
                
                {passwordSuccess && (
                  <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-5 w-5" />
                    <span>{passwordSuccess}</span>
                  </div>
                )}
                
                {passwordError && (
                  <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    <span>{passwordError}</span>
                  </div>
                )}

                <form onSubmit={handlePasswordChangeRequest}>
                  <div className="space-y-4">
                    <div className="p-6 bg-[#222226] border border-[#3A3A3D] rounded-lg">
                      <h3 className="text-lg font-semibold text-white mb-2">Comment ça fonctionne ?</h3>
                      <ol className="list-decimal list-inside text-[#B8B8BA] space-y-2 text-sm">
                        <li>Cliquez sur le bouton ci-dessous</li>
                        <li>Vous recevrez un email à <strong className="text-white">{user?.email}</strong></li>
                        <li>Cliquez sur le lien dans l'email (valide 1 heure)</li>
                        <li>Définissez votre nouveau mot de passe</li>
                      </ol>
                    </div>

                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="w-full bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white py-3 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <Mail className="h-5 w-5" />
                          Recevoir le lien par email
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </div>

          {/* Revoir le guide */}
          <div className="px-8 pb-6">
            <button
              onClick={() => {
                localStorage.removeItem(ONBOARDING_KEY);
                localStorage.removeItem(VENDOR_ONBOARDING_KEY);
                window.location.href = user?.role === 'vendor' ? '/vendor' : '/client';
              }}
              className="flex items-center space-x-2 text-[#B8B8BA] hover:text-[#00FFFF] text-sm transition-colors"
              data-testid="replay-onboarding"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Revoir le guide de d&eacute;marrage</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountSettings;
